/**
 * @fileoverview Photo-Worthy Checklist Items Configuration
 * @module photo-worthy-items
 * 
 * Business Context:
 * - Defines which checklist items support inline photo uploads
 * - Provides item-specific prompts and guidance for photo documentation
 * - Maps photos to specific issues for better packet generation
 * 
 * Technical Context:
 * - 35 items identified as photo-worthy based on visual nature
 * - Each item includes category, prompt text, and guidance tips
 * - Used by ChecklistQuestion component to show inline upload
 */

// MaintenanceCategory type matches the backend enum
export type MaintenanceCategory = 'ROOF' | 'FOUNDATION' | 'HVAC' | 'PLUMBING' | 'ELECTRICAL';

export interface PhotoItemConfig {
  category: MaintenanceCategory;
  promptText: string;
  guidanceTips: string[];
}

/**
 * Configuration for checklist items that support inline photo uploads.
 * Photos uploaded for these items will be tagged with the checklist item ID
 * for proper categorization in appeal packets.
 */
export const PHOTO_WORTHY_ITEMS: Record<string, PhotoItemConfig> = {
  // ============================================
  // ROOF ITEMS (7 of 10)
  // ============================================
  
  visible_damage: {
    category: 'ROOF',
    promptText: 'Photo of damaged, curling, or missing shingles',
    guidanceTips: [
      'Take wide shots showing the overall damage area',
      'Get close-ups of specific damaged shingles',
      'Photograph from multiple angles if possible',
      'Include something for scale in close-up photos'
    ]
  },

  hail_damage: {
    category: 'ROOF',
    promptText: 'Photo of hail damage evidence',
    guidanceTips: [
      'Photograph dark spots on shingles where granules are gone',
      'Include photos of dented gutters, vents, or AC units',
      'Show dents in metal surfaces for comparison',
      'Take photos of multiple affected areas'
    ]
  },

  leaks_stains: {
    category: 'ROOF',
    promptText: 'Photo of water stains on ceilings or walls',
    guidanceTips: [
      'Photograph all stained areas with good lighting',
      'Capture ring patterns showing repeated water exposure',
      'Include any bubbling or peeling paint',
      'Document mold or mildew growth if present'
    ]
  },

  granule_loss: {
    category: 'ROOF',
    promptText: 'Photo of granules in gutters or at downspouts',
    guidanceTips: [
      'Include a coin or ruler for scale',
      'Photograph granule piles at downspout discharge areas',
      'Show bare or shiny patches on shingles if visible',
      'Capture granules in white container for visibility'
    ]
  },

  sagging_areas: {
    category: 'ROOF',
    promptText: 'Photo showing roof line sagging or dipping',
    guidanceTips: [
      'Photograph from across the street for full view',
      'Capture the entire roof line showing the sag',
      'Use a straight edge in photo for reference',
      'Take photos from multiple angles'
    ]
  },

  flashing_damage: {
    category: 'ROOF',
    promptText: 'Photo of damaged flashing around vents or chimney',
    guidanceTips: [
      'Show visible rust, gaps, or lifting',
      'Photograph each flashing location separately',
      'Include any tar or caulk patches as evidence of repairs',
      'Document water stains below flashing areas'
    ]
  },

  wind_damage: {
    category: 'ROOF',
    promptText: 'Photo of wind damage from storms',
    guidanceTips: [
      'Photograph lifted or creased shingles',
      'Show missing shingles or exposed underlayment',
      'Capture debris impact damage',
      'Include damaged ridge caps along the peak'
    ]
  },

  // ============================================
  // FOUNDATION ITEMS (9 of 10)
  // ============================================

  visible_cracks: {
    category: 'FOUNDATION',
    promptText: 'Photo of foundation cracks',
    guidanceTips: [
      'Include a ruler or tape measure for scale',
      'Measure and photograph the widest point',
      'Document the full length of each crack',
      'Note if cracks show displacement (one side higher)'
    ]
  },

  stair_step_cracks: {
    category: 'FOUNDATION',
    promptText: 'Photo of stair-step cracks in brick',
    guidanceTips: [
      'Capture the diagonal stair-step pattern clearly',
      'Include wide shots showing the full crack pattern',
      'Get close-ups showing crack width',
      'Document all walls with stair-step cracks'
    ]
  },

  horizontal_cracks: {
    category: 'FOUNDATION',
    promptText: 'Photo of horizontal foundation cracks',
    guidanceTips: [
      'Photograph the full length of horizontal cracks',
      'Document any wall bowing with photos',
      'Measure crack width at multiple points',
      'Include any water seepage or staining'
    ]
  },

  doors_windows_stick: {
    category: 'FOUNDATION',
    promptText: 'Photo of gaps around doors or windows',
    guidanceTips: [
      'Include a ruler showing gap size',
      'Photograph visible daylight around exterior doors',
      'Show misaligned latches and strike plates',
      'Document gaps between frames and walls'
    ]
  },

  uneven_floors: {
    category: 'FOUNDATION',
    promptText: 'Photo of level showing floor slope',
    guidanceTips: [
      'Photograph a level on the floor showing tilt',
      'Video a marble or ball rolling across floor',
      'Show gaps between floor and baseboards',
      'Document multiple rooms if affected'
    ]
  },

  gaps_walls_ceiling: {
    category: 'FOUNDATION',
    promptText: 'Photo of gaps between walls and ceiling/floor',
    guidanceTips: [
      'Include a ruler for scale',
      'Photograph gaps at wall-ceiling joints',
      'Document separated baseboards from walls',
      'Show cracks in crown molding'
    ]
  },

  soil_separation: {
    category: 'FOUNDATION',
    promptText: 'Photo of gap between soil and foundation',
    guidanceTips: [
      'Include a ruler showing gap width',
      'Photograph during dry weather when gap is visible',
      'Document the gap around all sides of house',
      'Show cracked soil surface near foundation'
    ]
  },

  pier_beam_issues: {
    category: 'FOUNDATION',
    promptText: 'Photo of pier and beam damage',
    guidanceTips: [
      'Photograph visible rot or water damage on beams',
      'Document shifted or settling piers',
      'Show evidence of termite or pest damage',
      'Include wide shots of crawl space condition'
    ]
  },

  previous_repairs: {
    category: 'FOUNDATION',
    promptText: 'Photo of previous foundation repair evidence',
    guidanceTips: [
      'Photograph circular patches in concrete (mudjacking)',
      'Document visible steel pier brackets',
      'Show sections of newer concrete',
      'Include any visible repair hardware'
    ]
  },

  // ============================================
  // HVAC ITEMS (2 of 10)
  // ============================================

  age_over_10: {
    category: 'HVAC',
    promptText: 'Photo of HVAC system data plate',
    guidanceTips: [
      'Photograph the data plate on outdoor unit',
      'Capture make, model, and serial number clearly',
      'Include any visible SEER rating label',
      'Show overall condition of the unit'
    ]
  },

  ductwork_issues: {
    category: 'HVAC',
    promptText: 'Photo of damaged ductwork',
    guidanceTips: [
      'Photograph disconnected or hanging sections',
      'Document visible holes or tears',
      'Show deteriorated or missing insulation',
      'Capture crushed or kinked flexible ducts'
    ]
  },

  // ============================================
  // PLUMBING ITEMS (8 of 11)
  // ============================================

  pipe_age: {
    category: 'PLUMBING',
    promptText: 'Photo of exposed pipes showing material type',
    guidanceTips: [
      'Photograph pipes under sinks or in utility areas',
      'Show corrosion buildup on galvanized pipes',
      'Capture any mix of pipe materials',
      'Include close-ups showing pipe condition'
    ]
  },

  low_water_pressure: {
    category: 'PLUMBING',
    promptText: 'Photo of pressure gauge reading',
    guidanceTips: [
      'Show pressure gauge reading at hose bib',
      'Document the pressure number clearly',
      'Normal pressure is 40-60 PSI',
      'Take photo during peak usage if possible'
    ]
  },

  water_discoloration: {
    category: 'PLUMBING',
    promptText: 'Photo of discolored water in white container',
    guidanceTips: [
      'Collect water in a white container for visibility',
      'Place next to clear bottled water for comparison',
      'Document if hot water or cold water is affected',
      'Show any visible sediment or particles'
    ]
  },

  visible_leaks: {
    category: 'PLUMBING',
    promptText: 'Photo of visible leaks or water damage',
    guidanceTips: [
      'Photograph dripping faucets or wet areas',
      'Document water stains or damage from leaks',
      'Show corrosion or mineral buildup at connections',
      'Include all leak locations in photos'
    ]
  },

  water_heater_issues: {
    category: 'PLUMBING',
    promptText: 'Photo of water heater condition and data plate',
    guidanceTips: [
      'Photograph the data plate showing manufacture date',
      'Document any visible rust on tank',
      'Show water pooling around base if present',
      'Capture overall condition of the unit'
    ]
  },

  slab_leak: {
    category: 'PLUMBING',
    promptText: 'Photo of slab leak indicators',
    guidanceTips: [
      'Video water meter with all fixtures off',
      'Document warm or wet spots on floors',
      'Show water bills with unexplained increases',
      'Capture any foundation cracks near suspected leak'
    ]
  },

  polybutylene_pipes: {
    category: 'PLUMBING',
    promptText: 'Photo of gray polybutylene pipes',
    guidanceTips: [
      'Show the gray color of the pipes clearly',
      'Get close-up of PB2110 stamping if visible',
      'Photograph copper crimp rings at connections',
      'Document all locations where PB pipes are found'
    ]
  },

  freeze_damage: {
    category: 'PLUMBING',
    promptText: 'Photo of freeze damage or repairs',
    guidanceTips: [
      'Document patched or repaired pipe sections',
      'Show new pipe materials mixed with old',
      'Photograph any temporary repairs still in place',
      'Include any water damage from bursts'
    ]
  },

  // ============================================
  // ELECTRICAL ITEMS (7 of 10)
  // ============================================

  panel_age: {
    category: 'ELECTRICAL',
    promptText: 'Photo of electrical panel data plate and condition',
    guidanceTips: [
      'Photograph the panel data plate clearly',
      'Document the amperage rating visible',
      'Show rust, corrosion, or discoloration',
      'Count empty breaker spaces if visible'
    ]
  },

  fuse_box: {
    category: 'ELECTRICAL',
    promptText: 'Photo of fuse box',
    guidanceTips: [
      'Photograph the fuse box clearly',
      'Show the round screw-in fuses',
      'Document the amperage rating',
      'Capture overall condition'
    ]
  },

  panel_brand: {
    category: 'ELECTRICAL',
    promptText: 'Photo of electrical panel brand label',
    guidanceTips: [
      'Photograph the brand name on panel door',
      'Show Federal Pacific "Stab-Lok" label if present',
      'Capture Zinsco colored breaker handles',
      'Document any visible manufacturer markings'
    ]
  },

  two_prong_outlets: {
    category: 'ELECTRICAL',
    promptText: 'Photo of ungrounded 2-prong outlets',
    guidanceTips: [
      'Photograph outlets showing only two slots',
      'Document multiple outlets throughout home',
      'Show any adapters being used',
      'Include count of affected outlets if possible'
    ]
  },

  warm_outlets: {
    category: 'ELECTRICAL',
    promptText: 'Photo of warm outlet with temperature reading',
    guidanceTips: [
      'Use infrared thermometer if available',
      'Document temperature reading in photo',
      'Show any discoloration around outlet',
      'Note which outlets are affected'
    ]
  },

  aluminum_wiring: {
    category: 'ELECTRICAL',
    promptText: 'Photo showing aluminum wiring',
    guidanceTips: [
      'Photograph silver-colored wires in panel (outer cover only)',
      'Show AL or ALUMINUM marking on wire insulation',
      'Document exposed wiring in accessible areas',
      'Never remove inner panel cover'
    ]
  },

  insufficient_capacity: {
    category: 'ELECTRICAL',
    promptText: 'Photo of main breaker showing amperage',
    guidanceTips: [
      'Photograph the main breaker clearly',
      'Show the amperage rating (100A, 150A, 200A)',
      'Document if panel is full (no empty spaces)',
      'Capture any double-tapped breakers'
    ]
  }
};

/**
 * Check if a checklist item supports inline photo uploads
 */
export function isPhotoWorthyItem(itemId: string): boolean {
  return itemId in PHOTO_WORTHY_ITEMS;
}

/**
 * Get photo configuration for an item
 */
export function getPhotoConfig(itemId: string): PhotoItemConfig | undefined {
  return PHOTO_WORTHY_ITEMS[itemId];
}

/**
 * Get all photo-worthy items for a specific category
 */
export function getPhotoWorthyItemsForCategory(category: MaintenanceCategory): string[] {
  return Object.entries(PHOTO_WORTHY_ITEMS)
    .filter(([_, config]) => config.category === category)
    .map(([itemId]) => itemId);
}

/**
 * Get count of photo-worthy items per category
 */
export const PHOTO_WORTHY_COUNTS = {
  ROOF: 7,
  FOUNDATION: 9,
  HVAC: 2,
  PLUMBING: 8,
  ELECTRICAL: 7
} as const;
